// src/components/BuildingDesigner/PlanCanvas.jsx
//
// The interactive SVG canvas. Renders:
//   - bounding box + adaptive sub-grid (1 m → 10 cm → 1 cm as you zoom in)
//   - already-added floor elements (rooms + their doors/windows/open-doors)
//   - live preview of the element being drawn now
//   - live preview of the opening being placed now
//   - cursor coordinate readout (snapped to the current grid step)
//
// Interactions:
//   - mouse wheel = zoom, centred on the cursor
//   - click = report a snapped world-coord to the parent via onCanvasClick
//   - drag = pan (right-mouse or Shift+left) — optional, kept simple
//
// The parent decides what a click means (rect corner? opening hinge?
// etc.) based on the current mode.

import React, { useRef, useEffect, useState } from "react";
import {
  snap,
  chooseGridStep,
  rectOpeningEndpoints,
  rectDoorEndpoints,
  rectOpenDoorEndpoints,
  rectWindowEndpoints,
  polygonCentroid,
  customDoorEndpoints,
  customWindowEndpoints,
  customOpenDoorEndpoints,
  edgeInfoFromIndex,
  edgeKeyToIndex,
} from "./geoUtils";

const AXIS = "#666";
const GRID_1M = "#d0d0d0";
const GRID_10CM = "#e8e8e8";
const GRID_1CM = "#f2f2f2";
const ELEM_STROKE = "#333";
const ELEM_FILL = "#e0f2f1"; // light teal
const PREVIEW_STROKE = "teal";
const PREVIEW_FILL = "rgba(0,128,128,0.15)";
const DOOR_COLOR = "#c0392b";
const WINDOW_COLOR = "#2e86de";
const OPEN_COLOR = "#7f8c8d";

// ROOM_TYPE_COLORS could be added later; keep it simple for now.

export default function PlanCanvas({
  bbox,
  floor,
  drawing, // { type, x, y, width, height, doors, windows, open_doors } or null
  previewOpening, // ephemeral opening being placed (see below)
  onCanvasClick, // (worldX, worldY, {ctrl}) → void
  onCanvasMove, // (worldX, worldY) → void  (optional)
  clickMode, // "corner" | "twoCorner"
  selection = [], // Stage 3u ship 1: [{kind:"wall|room|...", index:n}, ...]
  showTexts = true, // Stage 3x.5: draw text labels from floor.texts
  hoverInfo = null, // Stage 4m: {text, x, y} hover-tooltip payload
  hasActiveTool = false, // Stage 4m: keep cursor as arrow when no tool
  mirrorPreview = null, // Stage 5b: {axis, line} — dashed mirror line
  onRubberBandSelect, // Stage 5c: (worldRect, {ctrl}) => void
  pastePreview = null, // Stage 5d: {x,y,items,refX,refY} — paste ghost
  isEditing = false, // Stage 6a: is an element currently open for edit?
  inputStep = 0.1, // Stage 6a: drag-handle snap precision (0.1 | 0.01)
  onElementDrag, // Stage 6a: (patch) => void — merged into `drawing`
  groupPendingChange = null, // Stage 8d: { rooms: {idx:{x,y,width,height,
  //   doors,windows,open_doors}}, nonRoomDx,
  //   nonRoomDy, lastGesture } — fully
  //   resolved live preview (rendering only,
  //   computed on the page, until confirmed)
  onGroupDragStart, // Stage 8d: () => void — fires once at
  // mousedown, before any movement
  onGroupDragMove, // Stage 6d: (dx,dy) => void — continuous, drives
  // groupPendingChange (move) while dragging
  onGroupDragCommit, // Stage 6d: (dx,dy) => void — fires once on
  // mouseup (keeps it pending, doesn't apply yet)
  onGroupResizeStart, // Stage 8d: () => void — fires once at
  // mousedown, before any movement
  onGroupResizeMove, // Stage 6f: (edge,delta) => void — continuous,
  // drives groupPendingChange (resize)
  onGroupResizeCommit, // Stage 6f: (edge,delta) => void — fires once
  // on mouseup (keeps it pending)
}) {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ w: 800, h: 600 });

  // Zoom: pixels-per-metre relative to the auto-fit scale.
  // 1.0 = auto-fit; 2.0 = 2× zoom; wheel scrolls this.
  const [zoom, setZoom] = useState(1.0);
  const [pan, setPan] = useState({ dx: 0, dy: 0 });

  // Live cursor position in world coords (snapped).
  const [cursor, setCursor] = useState({ x: null, y: null });

  // Size the SVG to fill its parent. useLayoutEffect measures BEFORE
  // paint, so the first render already has the right size. ResizeObserver
  // then keeps it in sync on window resize / panel changes.
  React.useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth || el.offsetWidth || 800;
      const h = el.clientHeight || el.offsetHeight || 600;
      // Upper cap prevents runaway measurements from a broken parent
      // (we once saw an SVG go to 212,000 px tall). Cap at viewport size.
      const wMax = Math.min(4000, window.innerWidth || 4000);
      const hMax = Math.min(4000, window.innerHeight || 4000);
      setSize({
        w: Math.max(200, Math.min(w, wMax)),
        h: Math.max(200, Math.min(h, hMax)),
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  // Compute the pixels-per-metre so bbox fits with padding at zoom = 1.
  const padding = 40;
  const availW = size.w - 2 * padding;
  const availH = size.h - 2 * padding;
  const ppmFit = Math.max(
    1,
    Math.min(availW / bbox.width, availH / bbox.height)
  );
  const ppm = ppmFit * zoom;

  const drawW = bbox.width * ppm;
  const drawH = bbox.height * ppm;

  // Origin (world 0,0) in SVG coordinates. Y is flipped (SVG down = +y up in world).
  // Content doesn't always start near world (0,0) — a file imported from a
  // real survey/DXF can have coordinates offset by thousands of units.
  // bbox.offsetX/offsetY (the content's own SW corner) shift the origin so
  // the view centres on the CONTENT, not always on absolute world zero.
  const offsetX = bbox.offsetX || 0;
  const offsetY = bbox.offsetY || 0;
  const originX = (size.w - drawW) / 2 + pan.dx - offsetX * ppm;
  const originY = (size.h + drawH) / 2 + pan.dy + offsetY * ppm;

  const worldToSvg = (wx, wy) => ({
    px: originX + wx * ppm,
    py: originY - wy * ppm,
  });
  const svgToWorld = (px, py) => ({
    x: (px - originX) / ppm,
    y: (originY - py) / ppm,
  });

  const gridStep = chooseGridStep(ppm); // 1, 0.1, or 0.01 m

  // ── Event handlers ──────────────────────────────────────────────────
  // Stage 6c fix: ALWAYS measure against the outer container's bounding
  // box (containerRef), never e.currentTarget. Drag handles attach their
  // own onMouseDown directly to small SVG children (resize handles, the
  // room's interior fill, wall endpoints, polygon vertices/fill) — for
  // those events e.currentTarget is THAT CHILD element, not the canvas,
  // so getBoundingClientRect() on it returns a completely different
  // (and much smaller) reference frame, producing wildly wrong world
  // coordinates. containerRef wraps the whole canvas and is stable
  // regardless of which element the event originated from.
  const localXY = (e) => {
    const r = containerRef.current
      ? containerRef.current.getBoundingClientRect()
      : e.currentTarget.getBoundingClientRect();
    return { px: e.clientX - r.left, py: e.clientY - r.top };
  };

  // Pan the canvas by dragging with the middle mouse button OR
  // Alt+left-click drag. Simple hand tool.
  const panRef = useRef(null); // { startPx, startPy, startDx, startDy }

  const isPanTrigger = (e) => e.button === 1 || e.altKey;

  // Stage 5c: rubber-band (drag-box) selection. Only armed when no
  // drawing tool is running (hasActiveTool === false) — otherwise plain
  // clicks are needed for placing points/corners as before. A movement
  // threshold distinguishes a genuine drag from a simple click so single
  // clicks keep working exactly as before (edit-open, ctrl-toggle, etc).
  const dragSelectRef = useRef(null); // { startPx, startPy, startWorld, active }
  const [dragSelectBox, setDragSelectBox] = useState(null); // px rect for render
  // Stage 8f: cursor to show while hovering a multi-selection's move/
  // resize zones (idle, not dragging) — "move" | "ns-resize" |
  // "ew-resize" | null.
  const [hoverCursor, setHoverCursor] = useState(null);
  const suppressClickRef = useRef(false);
  const DRAG_THRESHOLD_PX = 5;

  // Stage 6a: interactive drag-handles for the element currently being
  // edited (isEditing === true). Handles are attached directly to SVG
  // shapes below (corner/edge circles, wall endpoints, custom vertices,
  // shaft/stairs move-rect) via their own onMouseDown, which calls
  // startElementDrag() to arm this ref. The shared handleMove/handleMouseUp
  // below then drive the drag exactly like rubber-band-select does.
  //   elementDragRef = { mode, extra, startPx, startPy, orig, active }
  const elementDragRef = useRef(null);
  const MIN_DRAG_SIZE = 0.2; // soft floor in metres — avoids a
  // degenerate/negative box mid-drag; the
  // real per-type minimum is still enforced
  // by the form's disabled Save/Add button.

  const roundStep = (v) => snap(v, inputStep);

  const startElementDrag = (e, mode, orig, extra) => {
    e.stopPropagation();
    e.preventDefault();
    const { px, py } = localXY(e);
    const startWorld = svgToWorld(px, py);
    elementDragRef.current = {
      mode,
      extra,
      startPx: px,
      startPy: py,
      startWorld,
      orig,
      active: false,
    };
  };

  // Stage 6f: multi-selection group RESIZE. Dragging near an EDGE (wall
  // line) of any SELECTED room applies the SAME width/height delta to
  // EVERY selected room — e.g. drag one room's south edge in by 0.3m,
  // every selected room's height shrinks by 0.3m too (each keeping its
  // own opposite edge fixed). Only ROOM-kind selections participate;
  // walls/customs/shafts/stairs in the same selection are untouched by
  // a resize (they only move together via groupDragRef above).
  //   groupResizeRef = { edge, startPx, startPy, startWorld, active,
  //                       refOrig: {x,y,width,height},   // the clicked room
  //                       snapshot: [{index, orig:{x,y,width,height}}] }
  const groupResizeRef = useRef(null);
  const EDGE_HIT_TOL = 0.2; // metres — how close counts as "on the edge"

  // Which edge (S/N/W/E) of room r is world point (wx,wy) near, if any?
  const nearRoomEdge = (r, wx, wy) => {
    const x0 = r.x,
      y0 = r.y,
      x1 = r.x + r.width,
      y1 = r.y + r.height;
    const withinX = wx >= x0 - EDGE_HIT_TOL && wx <= x1 + EDGE_HIT_TOL;
    const withinY = wy >= y0 - EDGE_HIT_TOL && wy <= y1 + EDGE_HIT_TOL;
    if (withinX && Math.abs(wy - y0) <= EDGE_HIT_TOL) return "S";
    if (withinX && Math.abs(wy - y1) <= EDGE_HIT_TOL) return "N";
    if (withinY && Math.abs(wx - x0) <= EDGE_HIT_TOL) return "W";
    if (withinY && Math.abs(wx - x1) <= EDGE_HIT_TOL) return "E";
    return null;
  };

  // Stage 6d: multi-selection group move. Dragging from ON TOP OF any
  // currently-selected element moves the WHOLE selection together (pure
  // translation, no resize, walls move as rigid units). Dragging from
  // empty space or an UNSELECTED element instead falls through to the
  // existing rubber-band-select behaviour further down.
  //   groupDragRef = { startPx, startPy, startWorld, active,
  //                    snapshot: [{kind,index,orig}] }
  const groupDragRef = useRef(null);

  const isSelected = (kind, index) =>
    selection.some((s) => s.kind === kind && s.index === index);

  // Stage 8f: resolve a selected element's EFFECTIVE (current) geometry
  // — the pending group-change value if one exists for it, otherwise
  // the original floor data. Used by hit-testing (so a SECOND drag
  // grabs the room where it now VISUALLY is, not its stale pre-drag
  // position/size) and by the selection highlight (so the dashed box
  // tracks the live preview instead of staying frozen at the original
  // position/size while the room itself visibly moves/resizes).
  const effectiveRoom = (i) => {
    const r = floor.rooms?.[i];
    if (!r) return r;
    const patch = groupPendingChange?.rooms?.[i];
    return patch ? { ...r, ...patch } : r;
  };
  const effectiveNonRoomShift = () =>
    groupPendingChange
      ? {
          dx: groupPendingChange.nonRoomDx || 0,
          dy: groupPendingChange.nonRoomDy || 0,
        }
      : { dx: 0, dy: 0 };
  const effectiveShaft = (i) => {
    const s = floor.shafts?.[i];
    if (!s) return s;
    const { dx, dy } = effectiveNonRoomShift();
    return dx || dy ? { ...s, x: s.x + dx, y: s.y + dy } : s;
  };
  const effectiveStairs = (i) => {
    const s = floor.stairs?.[i];
    if (!s) return s;
    const { dx, dy } = effectiveNonRoomShift();
    return dx || dy ? { ...s, x: s.x + dx, y: s.y + dy } : s;
  };
  const effectiveWall = (i) => {
    const w = floor.beams?.[i];
    if (!w) return w;
    const { dx, dy } = effectiveNonRoomShift();
    return dx || dy
      ? { ...w, x1: w.x1 + dx, y1: w.y1 + dy, x2: w.x2 + dx, y2: w.y2 + dy }
      : w;
  };
  const effectiveCustomPoints = (i) => {
    const c = floor.customs?.[i];
    const pts = c?.points || [];
    const { dx, dy } = effectiveNonRoomShift();
    return dx || dy ? pts.map(([px, py]) => [px + dx, py + dy]) : pts;
  };

  // Given a world point, return the (kind,index) of the topmost SELECTED
  // element under it, or null. Same geometric tests used elsewhere in
  // this file (bbox for room/shaft/stairs, perpendicular distance for
  // walls, point-in-polygon for customs).
  const hitTestSelectedAt = (wx, wy) => {
    // Rooms
    for (let i = 0; i < (floor.rooms || []).length; i++) {
      if (!isSelected("room", i)) continue;
      const r = effectiveRoom(i);
      if (
        wx >= r.x &&
        wx <= r.x + r.width &&
        wy >= r.y &&
        wy <= r.y + r.height
      ) {
        return { kind: "room", index: i };
      }
    }
    // Shafts
    for (let i = 0; i < (floor.shafts || []).length; i++) {
      if (!isSelected("shaft", i)) continue;
      const s = effectiveShaft(i);
      const b = shaftBlock(s);
      if (
        wx >= s.x &&
        wx <= s.x + b.width &&
        wy >= s.y &&
        wy <= s.y + b.height
      ) {
        return { kind: "shaft", index: i };
      }
    }
    // Stairs
    for (let i = 0; i < (floor.stairs || []).length; i++) {
      if (!isSelected("stairs", i)) continue;
      const s = effectiveStairs(i);
      const b = stairsBlock(s);
      if (
        wx >= s.x &&
        wx <= s.x + b.width &&
        wy >= s.y &&
        wy <= s.y + b.height
      ) {
        return { kind: "stairs", index: i };
      }
    }
    // Customs (point-in-polygon)
    for (let i = 0; i < (floor.customs || []).length; i++) {
      if (!isSelected("custom", i)) continue;
      const pts = effectiveCustomPoints(i);
      if (pts.length < 3) continue;
      let inside = false;
      for (let a = 0, b = pts.length - 1; a < pts.length; b = a++) {
        const [xa, ya] = pts[a],
          [xb, yb] = pts[b];
        if (
          ya > wy !== yb > wy &&
          wx < ((xb - xa) * (wy - ya)) / (yb - ya + 1e-12) + xa
        ) {
          inside = !inside;
        }
      }
      if (inside) return { kind: "custom", index: i };
    }
    // Walls (perpendicular distance to segment)
    for (let i = 0; i < (floor.beams || []).length; i++) {
      if (!isSelected("beam", i)) continue;
      const w = effectiveWall(i);
      const dx = w.x2 - w.x1,
        dy = w.y2 - w.y1;
      const L2 = dx * dx + dy * dy;
      if (L2 < 1e-9) continue;
      let u = ((wx - w.x1) * dx + (wy - w.y1) * dy) / L2;
      if (u < 0) u = 0;
      else if (u > 1) u = 1;
      const px2 = w.x1 + u * dx,
        py2 = w.y1 + u * dy;
      if (Math.hypot(wx - px2, wy - py2) <= 0.2) {
        return { kind: "beam", index: i };
      }
    }
    return null;
  };

  const handleMouseDown = (e) => {
    if (isPanTrigger(e)) {
      e.preventDefault();
      const { px, py } = localXY(e);
      panRef.current = {
        startPx: px,
        startPy: py,
        startDx: pan.dx,
        startDy: pan.dy,
      };
      return;
    }
    if (!hasActiveTool && e.button === 0) {
      const { px, py } = localXY(e);
      const { x, y } = svgToWorld(px, py);
      // Stage 6f: if there's an active multi-selection that includes at
      // least one ROOM, and this click lands NEAR one of that room's
      // edges, start a GROUP RESIZE drag — takes priority over group
      // move, since a click near the boundary is more likely intended
      // as "grab this wall" than "grab the whole room."
      if (selection.length > 0) {
        const selectedRoomIdxs = selection
          .filter((s) => s.kind === "room")
          .map((s) => s.index);
        for (const idx of selectedRoomIdxs) {
          const r = effectiveRoom(idx);
          if (!r) continue;
          const edge = nearRoomEdge(r, x, y);
          if (edge) {
            const snapshot = selectedRoomIdxs.map((i) => {
              const room = effectiveRoom(i);
              return {
                index: i,
                orig: {
                  x: room.x,
                  y: room.y,
                  width: room.width,
                  height: room.height,
                },
              };
            });
            if (onGroupResizeStart) onGroupResizeStart();
            groupResizeRef.current = {
              edge,
              startPx: px,
              startPy: py,
              startWorld: { x, y },
              active: false,
              refOrig: { x: r.x, y: r.y, width: r.width, height: r.height },
              snapshot,
            };
            return;
          }
        }
      }
      // Stage 6d: if there's an active multi-selection and this click
      // lands ON one of the selected elements, start a GROUP MOVE drag
      // instead of a rubber-band select.
      if (selection.length > 0) {
        const hit = hitTestSelectedAt(x, y);
        if (hit) {
          const snapshot = selection.map((s) => {
            let orig;
            if (s.kind === "room") {
              const r = floor.rooms[s.index];
              orig = { x: r.x, y: r.y };
            } else if (s.kind === "beam") {
              const w = floor.beams[s.index];
              orig = { x1: w.x1, y1: w.y1, x2: w.x2, y2: w.y2 };
            } else if (s.kind === "custom") {
              orig = { points: floor.customs[s.index].points };
            } else if (s.kind === "shaft") {
              const sh = floor.shafts[s.index];
              orig = { x: sh.x, y: sh.y };
            } else if (s.kind === "stairs") {
              const st = floor.stairs[s.index];
              orig = { x: st.x, y: st.y };
            }
            return { kind: s.kind, index: s.index, orig };
          });
          if (onGroupDragStart) onGroupDragStart();
          groupDragRef.current = {
            startPx: px,
            startPy: py,
            startWorld: { x, y },
            active: false,
            snapshot,
          };
          return;
        }
      }
      dragSelectRef.current = {
        startPx: px,
        startPy: py,
        startWorld: { x, y },
        active: false,
      };
    }
  };

  const handleMove = (e) => {
    if (panRef.current) {
      const { px, py } = localXY(e);
      setPan({
        dx: panRef.current.startDx + (px - panRef.current.startPx),
        dy: panRef.current.startDy + (py - panRef.current.startPy),
      });
      return;
    }
    // Stage 6a: drive an active element-handle drag (resize/move of the
    // element currently open for edit). Computes a geometry patch per
    // `mode` and reports it via onElementDrag on every tick so the form
    // fields (bound to `drawing`) update live, matching the mouse.
    if (elementDragRef.current) {
      const { px, py } = localXY(e);
      const d = elementDragRef.current;
      if (!d.active) {
        const dist = Math.hypot(px - d.startPx, py - d.startPy);
        if (dist > DRAG_THRESHOLD_PX) d.active = true;
      }
      if (d.active && onElementDrag) {
        const { x: mx, y: my } = svgToWorld(px, py);
        const smx = roundStep(mx),
          smy = roundStep(my);
        const { mode, orig } = d;

        if (mode.startsWith("room-corner-")) {
          const which = mode.slice("room-corner-".length); // SW|SE|NE|NW
          const fixed = {
            SW: [orig.x + orig.width, orig.y + orig.height],
            SE: [orig.x, orig.y + orig.height],
            NE: [orig.x, orig.y],
            NW: [orig.x + orig.width, orig.y],
          }[which];
          const [fx, fy] = fixed;
          const newX = Math.min(fx, smx),
            newY = Math.min(fy, smy);
          const newW = Math.max(MIN_DRAG_SIZE, Math.abs(smx - fx));
          const newH = Math.max(MIN_DRAG_SIZE, Math.abs(smy - fy));
          onElementDrag({
            x: +newX.toFixed(3),
            y: +newY.toFixed(3),
            width: +newW.toFixed(3),
            height: +newH.toFixed(3),
          });
        } else if (mode.startsWith("room-edge-")) {
          const which = mode.slice("room-edge-".length); // S|N|W|E
          if (which === "S" || which === "N") {
            const fixedY = which === "S" ? orig.y + orig.height : orig.y;
            const newY = Math.min(fixedY, smy);
            const newH = Math.max(MIN_DRAG_SIZE, Math.abs(smy - fixedY));
            onElementDrag({
              x: orig.x,
              y: +newY.toFixed(3),
              width: orig.width,
              height: +newH.toFixed(3),
            });
          } else {
            const fixedX = which === "W" ? orig.x + orig.width : orig.x;
            const newX = Math.min(fixedX, smx);
            const newW = Math.max(MIN_DRAG_SIZE, Math.abs(smx - fixedX));
            onElementDrag({
              x: +newX.toFixed(3),
              y: orig.y,
              width: +newW.toFixed(3),
              height: orig.height,
            });
          }
        } else if (mode === "room-move") {
          const dx = roundStep(mx - d.startWorld.x);
          const dy = roundStep(my - d.startWorld.y);
          onElementDrag({
            x: +(orig.x + dx).toFixed(3),
            y: +(orig.y + dy).toFixed(3),
            width: orig.width,
            height: orig.height,
          });
        } else if (mode === "wall-p1") {
          onElementDrag({ x1: smx, y1: smy, x2: orig.x2, y2: orig.y2 });
        } else if (mode === "wall-p2") {
          onElementDrag({ x2: smx, y2: smy, x1: orig.x1, y1: orig.y1 });
        } else if (mode === "wall-move") {
          const dx = roundStep(mx - d.startWorld.x);
          const dy = roundStep(my - d.startWorld.y);
          onElementDrag({
            x1: +(orig.x1 + dx).toFixed(3),
            y1: +(orig.y1 + dy).toFixed(3),
            x2: +(orig.x2 + dx).toFixed(3),
            y2: +(orig.y2 + dy).toFixed(3),
          });
        } else if (mode === "custom-vertex") {
          const idx = d.extra.index;
          const nextPts = orig.points.map((p, i) =>
            i === idx ? [smx, smy] : p
          );
          onElementDrag({ points: nextPts });
        } else if (mode === "custom-move") {
          const dx = roundStep(mx - d.startWorld.x);
          const dy = roundStep(my - d.startWorld.y);
          const nextPts = orig.points.map(([px2, py2]) => [
            +(px2 + dx).toFixed(3),
            +(py2 + dy).toFixed(3),
          ]);
          onElementDrag({ points: nextPts });
        } else if (mode === "block-move") {
          const dx = roundStep(mx - d.startWorld.x);
          const dy = roundStep(my - d.startWorld.y);
          onElementDrag({
            x: +(orig.x + dx).toFixed(3),
            y: +(orig.y + dy).toFixed(3),
          });
        }
      }
      return;
    }
    if (groupResizeRef.current) {
      const { px, py } = localXY(e);
      const d = groupResizeRef.current;
      if (!d.active) {
        const dist = Math.hypot(px - d.startPx, py - d.startPy);
        if (dist > DRAG_THRESHOLD_PX) d.active = true;
      }
      if (d.active && onGroupResizeMove) {
        const { x: mx, y: my } = svgToWorld(px, py);
        const smx = roundStep(mx),
          smy = roundStep(my);
        const { edge, refOrig } = d;
        let delta;
        if (edge === "S") {
          const fixedY = refOrig.y + refOrig.height;
          const newH = Math.max(MIN_DRAG_SIZE, Math.abs(smy - fixedY));
          delta = newH - refOrig.height;
        } else if (edge === "N") {
          const fixedY = refOrig.y;
          const newH = Math.max(MIN_DRAG_SIZE, smy - fixedY);
          delta = newH - refOrig.height;
        } else if (edge === "W") {
          const fixedX = refOrig.x + refOrig.width;
          const newW = Math.max(MIN_DRAG_SIZE, Math.abs(smx - fixedX));
          delta = newW - refOrig.width;
        } else {
          // "E"
          const fixedX = refOrig.x;
          const newW = Math.max(MIN_DRAG_SIZE, smx - fixedX);
          delta = newW - refOrig.width;
        }
        onGroupResizeMove(edge, +delta.toFixed(3));
      }
      return;
    }
    if (groupDragRef.current) {
      const { px, py } = localXY(e);
      const d = groupDragRef.current;
      if (!d.active) {
        const dist = Math.hypot(px - d.startPx, py - d.startPy);
        if (dist > DRAG_THRESHOLD_PX) d.active = true;
      }
      if (d.active && onGroupDragMove) {
        const { x: mx, y: my } = svgToWorld(px, py);
        const dx = roundStep(mx - d.startWorld.x);
        const dy = roundStep(my - d.startWorld.y);
        onGroupDragMove(dx, dy);
      }
      return;
    }
    if (dragSelectRef.current) {
      const { px, py } = localXY(e);
      const d = dragSelectRef.current;
      if (!d.active) {
        const dist = Math.hypot(px - d.startPx, py - d.startPy);
        if (dist > DRAG_THRESHOLD_PX) d.active = true;
      }
      if (d.active) {
        setDragSelectBox({
          x1: Math.min(d.startPx, px),
          y1: Math.min(d.startPy, py),
          x2: Math.max(d.startPx, px),
          y2: Math.max(d.startPy, py),
        });
      }
    }
    const { px, py } = localXY(e);
    const { x, y } = svgToWorld(px, py);
    const sx = snap(x, gridStep);
    const sy = snap(y, gridStep);
    setCursor({ x: sx, y: sy });
    if (onCanvasMove) onCanvasMove(sx, sy);

    // Stage 8f: hover-cursor feedback for a multi-selection, matching
    // single-element editing's resize/move cursors. Only computed when
    // idle (nothing is currently being dragged — every drag branch
    // above already returned before reaching this point) and only
    // when there IS a selection to hover over.
    if (!hasActiveTool && selection.length > 0) {
      let cur = null;
      const selectedRoomIdxs = selection
        .filter((s) => s.kind === "room")
        .map((s) => s.index);
      for (const idx of selectedRoomIdxs) {
        const r = effectiveRoom(idx);
        if (!r) continue;
        const edge = nearRoomEdge(r, x, y);
        if (edge) {
          cur = edge === "S" || edge === "N" ? "ns-resize" : "ew-resize";
          break;
        }
      }
      if (!cur && hitTestSelectedAt(x, y)) cur = "move";
      if (cur !== hoverCursor) setHoverCursor(cur);
    } else if (hoverCursor) {
      setHoverCursor(null);
    }
  };

  const handleMouseUp = (e) => {
    if (panRef.current) {
      panRef.current = null;
      e.preventDefault();
      return;
    }
    if (elementDragRef.current) {
      // Any mousedown that started on a handle or the interior move
      // hit-area means the user is interacting with THIS element — even
      // a brief click with no real movement should NEVER fall through
      // to the old single-click "place a new corner here" placement
      // logic. Always suppress the synthetic click that follows.
      suppressClickRef.current = true;
      elementDragRef.current = null;
      return;
    }
    if (groupResizeRef.current) {
      const d = groupResizeRef.current;
      if (d.active) {
        const { px, py } = localXY(e);
        const { x: mx, y: my } = svgToWorld(px, py);
        const smx = roundStep(mx),
          smy = roundStep(my);
        const { edge, refOrig } = d;
        let delta;
        if (edge === "S") {
          const fixedY = refOrig.y + refOrig.height;
          const newH = Math.max(MIN_DRAG_SIZE, Math.abs(smy - fixedY));
          delta = newH - refOrig.height;
        } else if (edge === "N") {
          const fixedY = refOrig.y;
          const newH = Math.max(MIN_DRAG_SIZE, smy - fixedY);
          delta = newH - refOrig.height;
        } else if (edge === "W") {
          const fixedX = refOrig.x + refOrig.width;
          const newW = Math.max(MIN_DRAG_SIZE, Math.abs(smx - fixedX));
          delta = newW - refOrig.width;
        } else {
          const fixedX = refOrig.x;
          const newW = Math.max(MIN_DRAG_SIZE, smx - fixedX);
          delta = newW - refOrig.width;
        }
        if (onGroupResizeCommit) onGroupResizeCommit(edge, +delta.toFixed(3));
      }
      suppressClickRef.current = true;
      groupResizeRef.current = null;
      return;
    }
    if (groupDragRef.current) {
      const d = groupDragRef.current;
      if (d.active) {
        const { px, py } = localXY(e);
        const { x: mx, y: my } = svgToWorld(px, py);
        const dx = roundStep(mx - d.startWorld.x);
        const dy = roundStep(my - d.startWorld.y);
        if (onGroupDragCommit) onGroupDragCommit(dx, dy);
      }
      // Same reasoning as elementDragRef above — always suppress the
      // click that follows a mousedown on a selected element, even a
      // tiny one with no real movement.
      suppressClickRef.current = true;
      groupDragRef.current = null;
      return;
    }
    if (dragSelectRef.current) {
      const d = dragSelectRef.current;
      if (d.active) {
        const { px, py } = localXY(e);
        const { x: endX, y: endY } = svgToWorld(px, py);
        const rect = {
          minX: Math.min(d.startWorld.x, endX),
          maxX: Math.max(d.startWorld.x, endX),
          minY: Math.min(d.startWorld.y, endY),
          maxY: Math.max(d.startWorld.y, endY),
        };
        const ctrl = e.ctrlKey || e.metaKey;
        if (onRubberBandSelect) onRubberBandSelect(rect, { ctrl });
        // A real drag happened — the browser will still fire a native
        // "click" right after this mouseup; suppress just that one so
        // it doesn't ALSO run the normal single-click handling below.
        suppressClickRef.current = true;
      }
      dragSelectRef.current = null;
      setDragSelectBox(null);
    }
  };

  const handleLeave = () => {
    panRef.current = null;
    dragSelectRef.current = null;
    elementDragRef.current = null;
    groupDragRef.current = null;
    groupResizeRef.current = null;
    setDragSelectBox(null);
    setHoverCursor(null);
    setCursor({ x: null, y: null });
    if (onCanvasMove) onCanvasMove(null, null);
  };

  const handleClick = (e) => {
    // Ignore clicks that happened while panning (Alt held or middle mouse)
    if (isPanTrigger(e)) return;
    // Ignore the synthetic click that follows a real rubber-band drag —
    // it was already handled in handleMouseUp above.
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    if (!onCanvasClick) return;
    const { px, py } = localXY(e);
    const { x, y } = svgToWorld(px, py);
    const ctrl = e.ctrlKey || e.metaKey;
    // When Ctrl-clicking we're SELECTING an existing element, not
    // placing a new one, so skip the grid snap — otherwise a wall at
    // x=3.42 would never be hit by a click that snaps to 3.0.
    const sx = ctrl ? x : snap(x, gridStep);
    const sy = ctrl ? y : snap(y, gridStep);
    onCanvasClick(sx, sy, { ctrl });
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    const nextZoom = Math.max(0.5, Math.min(120, zoom * factor));
    // Zoom around the cursor
    const { px, py } = localXY(e);
    const { x, y } = svgToWorld(px, py);
    const newPpm = ppmFit * nextZoom;
    // We want the world (x,y) to stay under the cursor:
    //   px = newOriginX + x * newPpm   →   newOriginX = px - x * newPpm
    //   newOriginX = (size.w - bbox.width*newPpm)/2 + newDx
    const newOriginXWanted = px - x * newPpm;
    const newOriginYWanted = py + y * newPpm;
    // Must match the SAME offsetX/offsetY correction now in originX/
    // originY (see above) — otherwise zooming a file whose content sits
    // far from world (0,0) would make the content jump under the cursor
    // instead of staying put.
    const ox = bbox.offsetX || 0;
    const oy = bbox.offsetY || 0;
    const newDx =
      newOriginXWanted - (size.w - bbox.width * newPpm) / 2 + ox * newPpm;
    const newDy =
      newOriginYWanted - (size.h + bbox.height * newPpm) / 2 - oy * newPpm;
    setZoom(nextZoom);
    setPan({ dx: newDx, dy: newDy });
  };

  const resetView = () => {
    setZoom(1);
    setPan({ dx: 0, dy: 0 });
  };

  // ── Grid lines ──────────────────────────────────────────────────────
  // Iterate from the content's own offset (its SW corner), not always
  // from world 0 — a file whose content sits far from the origin needs
  // its grid drawn where the content actually is, snapped to whole
  // world-coordinate steps so the lines still land on nice round
  // numbers (e.g. ...,3394,3395,... not an arbitrary fraction).
  const grid = [];
  const majorStep = 1.0;
  const minorStep = gridStep;
  const gEndX = offsetX + bbox.width;
  const gEndY = offsetY + bbox.height;

  // Minor lines (10 cm or 1 cm), only within bbox, only if not the same as major
  if (minorStep < majorStep) {
    const minorColor = minorStep === 0.01 ? GRID_1CM : GRID_10CM;
    const minorStartX = Math.ceil(offsetX / minorStep) * minorStep;
    for (let mx = minorStartX; mx <= gEndX + 1e-9; mx += minorStep) {
      if (Math.abs(mx / majorStep - Math.round(mx / majorStep)) < 1e-9)
        continue;
      const { px } = worldToSvg(mx, 0);
      grid.push(
        <line
          key={`vm${mx.toFixed(3)}`}
          x1={px}
          y1={originY}
          x2={px}
          y2={originY - drawH}
          stroke={minorColor}
          strokeWidth={0.5}
        />
      );
    }
    const minorStartY = Math.ceil(offsetY / minorStep) * minorStep;
    for (let my = minorStartY; my <= gEndY + 1e-9; my += minorStep) {
      if (Math.abs(my / majorStep - Math.round(my / majorStep)) < 1e-9)
        continue;
      const { py } = worldToSvg(0, my);
      grid.push(
        <line
          key={`hm${my.toFixed(3)}`}
          x1={originX}
          y1={py}
          x2={originX + drawW}
          y2={py}
          stroke={minorColor}
          strokeWidth={0.5}
        />
      );
    }
  }
  // Major 1m lines
  const majorStartX = Math.ceil(offsetX / majorStep) * majorStep;
  for (let mx = majorStartX; mx <= gEndX + 1e-9; mx += majorStep) {
    const { px } = worldToSvg(mx, 0);
    grid.push(
      <line
        key={`v${mx}`}
        x1={px}
        y1={originY}
        x2={px}
        y2={originY - drawH}
        stroke={GRID_1M}
        strokeWidth={0.5}
      />
    );
  }
  const majorStartY = Math.ceil(offsetY / majorStep) * majorStep;
  for (let my = majorStartY; my <= gEndY + 1e-9; my += majorStep) {
    const { py } = worldToSvg(0, my);
    grid.push(
      <line
        key={`h${my}`}
        x1={originX}
        y1={py}
        x2={originX + drawW}
        y2={py}
        stroke={GRID_1M}
        strokeWidth={0.5}
      />
    );
  }

  // ── Drawing helpers ─────────────────────────────────────────────────
  const drawRect = (room, opts = {}) => {
    const p0 = worldToSvg(room.x, room.y + room.height); // NW
    const w = room.width * ppm,
      h = room.height * ppm;
    return (
      <g key={opts.key}>
        <rect
          x={p0.px}
          y={p0.py}
          width={w}
          height={h}
          fill={opts.fill || ELEM_FILL}
          stroke={opts.stroke || ELEM_STROKE}
          strokeWidth={opts.dashed ? 1.5 : 2}
          strokeDasharray={opts.dashed ? "6 4" : undefined}
          opacity={opts.opacity ?? 1}
          style={opts.onMouseDown ? { cursor: "move" } : undefined}
          onMouseDown={opts.onMouseDown}
        />
        {opts.label && (
          <text
            x={p0.px + w / 2}
            y={p0.py + h / 2}
            fontSize={12}
            fill="#333"
            textAnchor="middle"
            dominantBaseline="middle"
            pointerEvents="none"
          >
            {opts.label}
          </text>
        )}
      </g>
    );
  };

  // Stage 6a: 8 drag handles (4 corners + 4 edge midpoints) for a
  // rectangle currently open for edit. Rendered on TOP of the rect fill
  // (so they take priority over the interior move hit-area) and wired
  // to startElementDrag with mode "room-corner-XX" / "room-edge-X".
  const drawResizeHandles = (room) => {
    const x0 = room.x,
      y0 = room.y;
    const x1 = room.x + room.width,
      y1 = room.y + room.height;
    const midX = (x0 + x1) / 2,
      midY = (y0 + y1) / 2;
    const corners = [
      ["SW", x0, y0],
      ["SE", x1, y0],
      ["NE", x1, y1],
      ["NW", x0, y1],
    ];
    const edges = [
      ["S", midX, y0],
      ["N", midX, y1],
      ["W", x0, midY],
      ["E", x1, midY],
    ];
    const orig = {
      x: room.x,
      y: room.y,
      width: room.width,
      height: room.height,
    };
    return (
      <g key="resize-handles">
        {corners.map(([id, wx, wy]) => {
          const p = worldToSvg(wx, wy);
          return (
            <rect
              key={`corner-${id}`}
              x={p.px - 5}
              y={p.py - 5}
              width={10}
              height={10}
              fill="white"
              stroke={PREVIEW_STROKE}
              strokeWidth={1.5}
              style={{
                cursor:
                  id === "SW" || id === "NE" ? "nwse-resize" : "nesw-resize",
              }}
              onMouseDown={(e) =>
                startElementDrag(e, `room-corner-${id}`, orig)
              }
            />
          );
        })}
        {edges.map(([id, wx, wy]) => {
          const p = worldToSvg(wx, wy);
          const isVert = id === "S" || id === "N";
          return (
            <circle
              key={`edge-${id}`}
              cx={p.px}
              cy={p.py}
              r={5}
              fill="white"
              stroke={PREVIEW_STROKE}
              strokeWidth={1.5}
              style={{ cursor: isVert ? "ns-resize" : "ew-resize" }}
              onMouseDown={(e) => startElementDrag(e, `room-edge-${id}`, orig)}
            />
          );
        })}
      </g>
    );
  };

  const drawOpeningMarker = (room, o, kind, key) => {
    // Match Python's convention exactly:
    //   door       → position = hinge; gap extends per hinge_side
    //   window     → position = centre; gap is symmetric
    //   open door  → position = near edge; gap extends by width
    const [wa, wb] =
      kind === "door"
        ? rectDoorEndpoints(room, o.wall, o.position, o.width, o.hinge_side)
        : kind === "window"
          ? rectWindowEndpoints(room, o.wall, o.position, o.width)
          : rectOpenDoorEndpoints(room, o.wall, o.position, o.width);
    const pa = worldToSvg(wa[0], wa[1]);
    const pb = worldToSvg(wb[0], wb[1]);
    const color =
      kind === "door"
        ? DOOR_COLOR
        : kind === "window"
          ? WINDOW_COLOR
          : OPEN_COLOR;
    // Small end-caps at each end of the segment (perpendicular ticks) so
    // the exact endpoints are unambiguous on screen. NO offset dot — that
    // confused users into thinking the marker extended past the wall.
    const CAP = 5; // perpendicular tick length in pixels
    let capA1x = pa.px,
      capA1y = pa.py,
      capA2x = pa.px,
      capA2y = pa.py;
    let capB1x = pb.px,
      capB1y = pb.py,
      capB2x = pb.px,
      capB2y = pb.py;
    if (o.wall === "south" || o.wall === "north") {
      capA1y = pa.py - CAP;
      capA2y = pa.py + CAP;
      capB1y = pb.py - CAP;
      capB2y = pb.py + CAP;
    } else {
      // east/west
      capA1x = pa.px - CAP;
      capA2x = pa.px + CAP;
      capB1x = pb.px - CAP;
      capB2x = pb.px + CAP;
    }
    return (
      <g key={key} pointerEvents="none">
        {/* main opening segment — sits ON the wall itself */}
        <line
          x1={pa.px}
          y1={pa.py}
          x2={pb.px}
          y2={pb.py}
          stroke={color}
          strokeWidth={3}
          strokeLinecap="butt"
        />
        {/* end caps mark the exact endpoints */}
        <line
          x1={capA1x}
          y1={capA1y}
          x2={capA2x}
          y2={capA2y}
          stroke={color}
          strokeWidth={1.5}
        />
        <line
          x1={capB1x}
          y1={capB1y}
          x2={capB2x}
          y2={capB2y}
          stroke={color}
          strokeWidth={1.5}
        />
      </g>
    );
  };

  // Same style but for openings on custom-polygon edges. The wall is
  // "edgeN" so we resolve it back to the two endpoints of that edge, then
  // apply the same kind→endpoints logic as rectangles. End-cap ticks are
  // drawn perpendicular to the edge direction (not axis-aligned).
  const drawCustomOpeningMarker = (points, closed, o, kind, key) => {
    const idx = edgeKeyToIndex(o.wall);
    if (idx < 0 || idx >= points.length) return null;
    const nEdges = closed ? points.length : points.length - 1;
    if (idx >= nEdges) return null;
    const edgeInfo = edgeInfoFromIndex(points, idx, closed);
    const [wa, wb] =
      kind === "door"
        ? customDoorEndpoints(edgeInfo, o.position, o.width, o.hinge_side)
        : kind === "window"
          ? customWindowEndpoints(edgeInfo, o.position, o.width)
          : customOpenDoorEndpoints(edgeInfo, o.position, o.width);
    const pa = worldToSvg(wa[0], wa[1]);
    const pb = worldToSvg(wb[0], wb[1]);
    const color =
      kind === "door"
        ? DOOR_COLOR
        : kind === "window"
          ? WINDOW_COLOR
          : OPEN_COLOR;
    // Perpendicular direction in SVG pixel space.
    const dx = pb.px - pa.px,
      dy = pb.py - pa.py;
    const L = Math.hypot(dx, dy) || 1;
    const nx = -dy / L,
      ny = dx / L;
    const CAP = 5;
    return (
      <g key={key} pointerEvents="none">
        <line
          x1={pa.px}
          y1={pa.py}
          x2={pb.px}
          y2={pb.py}
          stroke={color}
          strokeWidth={3}
          strokeLinecap="butt"
        />
        <line
          x1={pa.px + nx * CAP}
          y1={pa.py + ny * CAP}
          x2={pa.px - nx * CAP}
          y2={pa.py - ny * CAP}
          stroke={color}
          strokeWidth={1.5}
        />
        <line
          x1={pb.px + nx * CAP}
          y1={pb.py + ny * CAP}
          x2={pb.px - nx * CAP}
          y2={pb.py - ny * CAP}
          stroke={color}
          strokeWidth={1.5}
        />
      </g>
    );
  };

  // ── Render the added rooms + their openings ────────────────────────
  // Stage 8d: `groupPendingChange.rooms[i]` (if present) already holds
  // the FULLY RESOLVED geometry + rescaled openings for this room —
  // computed once on the page (which also handles chaining adjacent
  // rooms sharing a wall along the resize axis, and stacking multiple
  // gestures on top of each other). This component just reads it, no
  // resize/chain math lives here any more.
  const addedElements = floor.rooms.map((r, i) => {
    let shifted = r;
    if (
      groupPendingChange &&
      groupPendingChange.rooms &&
      groupPendingChange.rooms[i]
    ) {
      shifted = { ...r, ...groupPendingChange.rooms[i] };
    }
    return (
      <React.Fragment key={`room-${i}`}>
        {drawRect(shifted, {
          key: `r-${i}`,
          label: shifted.label || shifted.name,
        })}
        {shifted.doors.map((d, j) =>
          drawOpeningMarker(shifted, d, "door", `d-${i}-${j}`)
        )}
        {shifted.windows.map((w, j) =>
          drawOpeningMarker(shifted, w, "window", `w-${i}-${j}`)
        )}
        {(shifted.open_doors || []).map((o, j) =>
          drawOpeningMarker(shifted, o, "open", `o-${i}-${j}`)
        )}
      </React.Fragment>
    );
  });

  // Simple polygon renderer — matches the rect style: light fill + thin
  // outline. Closed uses <polygon>, open uses <polyline>.
  const drawPolygon = (points, closed, opts = {}) => {
    if (!points || points.length < 2) return null;
    const pts = points
      .map(([x, y]) => {
        const p = worldToSvg(x, y);
        return `${p.px},${p.py}`;
      })
      .join(" ");
    const Tag = closed ? "polygon" : "polyline";
    const c = polygonCentroid(points);
    const cp = worldToSvg(c.x, c.y);
    return (
      <g key={opts.key}>
        <Tag
          points={pts}
          fill={closed ? opts.fill || ELEM_FILL : "none"}
          stroke={opts.stroke || ELEM_STROKE}
          strokeWidth={opts.dashed ? 1.5 : 2}
          strokeDasharray={opts.dashed ? "6 4" : undefined}
          opacity={opts.opacity ?? 1}
          strokeLinejoin="miter"
        />
        {opts.label && (
          <text
            x={cp.px}
            y={cp.py}
            fontSize={12}
            fill="#333"
            textAnchor="middle"
            dominantBaseline="middle"
            pointerEvents="none"
          >
            {opts.label}
          </text>
        )}
      </g>
    );
  };

  // Render committed customs (like rects but polygon-shaped).
  const addedCustoms = (floor.customs || []).map((c, i) => {
    const shiftedPts =
      groupPendingChange && isSelected("custom", i)
        ? (c.points || []).map(([px2, py2]) => [
            px2 + groupPendingChange.nonRoomDx,
            py2 + groupPendingChange.nonRoomDy,
          ])
        : c.points;
    return (
      <React.Fragment key={`custom-${i}`}>
        {drawPolygon(shiftedPts, c.closed, {
          key: `c-${i}`,
          label: c.label || c.name,
        })}
        {(c.doors || []).map((d, j) =>
          drawCustomOpeningMarker(
            shiftedPts,
            c.closed,
            d,
            "door",
            `cd-${i}-${j}`
          )
        )}
        {(c.windows || []).map((w, j) =>
          drawCustomOpeningMarker(
            shiftedPts,
            c.closed,
            w,
            "window",
            `cw-${i}-${j}`
          )
        )}
        {(c.open_doors || []).map((o, j) =>
          drawCustomOpeningMarker(
            shiftedPts,
            c.closed,
            o,
            "open",
            `co-${i}-${j}`
          )
        )}
      </React.Fragment>
    );
  });

  // Compute a shaft's block dimensions from its shape params — mirrors
  // Python StairShaft.block_w / block_h props exactly. Self-contained
  // to avoid a circular import from ElementForm.
  const shaftBlock = (s) => {
    const steps = Math.max(2, Math.floor(s.steps || 22));
    const stepWidth = Number(s.step_width || 1.5);
    const depth = Number(s.depth || 0.3);
    const separation = Number(s.separation || 0.3);
    const flat = Number(s.flat || 1.5);
    const widthDir = s.width_dir === "y" ? "y" : "x";
    const runLen = Math.floor(steps / 2) * depth + 2 * flat;
    const widthTotal = 2 * stepWidth + separation;
    return {
      width: widthDir === "x" ? widthTotal : runLen,
      height: widthDir === "x" ? runLen : widthTotal,
    };
  };

  // Render committed shafts. Same style as rooms but different fill so the
  // user visually distinguishes them.
  const SHAFT_FILL = "#fff4d5"; // pale yellow
  const ELEV_FILL = "#e4f0fb"; // pale blue
  const ELEV_STROKE = "#4a6fa5"; // muted navy
  const STAIRS_FILL = "#eae4d3"; // pale sand/gray — simple outdoor stairs

  // Compute a stairs block dimensions from its shape params.
  // Mirrors Python Stairs.block_w / block_h.
  const stairsBlock = (s) => {
    const steps = Math.max(2, Math.floor(s.steps || 5));
    const width = Number(s.width || 1.2);
    const depth = Number(s.depth || 0.3);
    const widthDir = s.width_dir === "y" ? "y" : "x";
    const runLen = steps * depth;
    return {
      width: widthDir === "x" ? width : runLen,
      height: widthDir === "x" ? runLen : width,
    };
  };

  // Render one elevator inside a shaft. Elevators are simple rectangles
  // (like rooms) with one open side — shown as a coloured gap on that wall.
  const drawElevator = (e, key) => {
    const w = Number(e.width || 1.6);
    const h = Number(e.height || 2.0);
    const p0 = worldToSvg(e.x, e.y + h); // NW corner in SVG
    const wpx = w * ppm,
      hpx = h * ppm;
    // Compute the open-side segment on the elevator's own wall.
    // opening_width is centred on that side.
    const side = e.opening_side || "south";
    const ow = Number(e.opening_width ?? 0.9);
    let opa, opb;
    if (side === "south") {
      const cx = e.x + w / 2;
      opa = worldToSvg(cx - ow / 2, e.y);
      opb = worldToSvg(cx + ow / 2, e.y);
    } else if (side === "north") {
      const cx = e.x + w / 2;
      opa = worldToSvg(cx - ow / 2, e.y + h);
      opb = worldToSvg(cx + ow / 2, e.y + h);
    } else if (side === "west") {
      const cy = e.y + h / 2;
      opa = worldToSvg(e.x, cy - ow / 2);
      opb = worldToSvg(e.x, cy + ow / 2);
    } else {
      // east
      const cy = e.y + h / 2;
      opa = worldToSvg(e.x + w, cy - ow / 2);
      opb = worldToSvg(e.x + w, cy + ow / 2);
    }
    // Diagonal X-cross inside (matches Python's elevator symbol).
    const p1 = worldToSvg(e.x, e.y);
    const p2 = worldToSvg(e.x + w, e.y);
    const p3 = worldToSvg(e.x + w, e.y + h);
    const p4 = worldToSvg(e.x, e.y + h);
    return (
      <g key={key} pointerEvents="none">
        <rect
          x={p0.px}
          y={p0.py}
          width={wpx}
          height={hpx}
          fill={ELEV_FILL}
          stroke={ELEV_STROKE}
          strokeWidth={1.5}
        />
        <line
          x1={p1.px}
          y1={p1.py}
          x2={p3.px}
          y2={p3.py}
          stroke={ELEV_STROKE}
          strokeWidth={1}
        />
        <line
          x1={p2.px}
          y1={p2.py}
          x2={p4.px}
          y2={p4.py}
          stroke={ELEV_STROKE}
          strokeWidth={1}
        />
        <line
          x1={opa.px}
          y1={opa.py}
          x2={opb.px}
          y2={opb.py}
          stroke="#ff6b6b"
          strokeWidth={3}
        />
      </g>
    );
  };

  const addedShafts = (floor.shafts || []).map((s, i) => {
    const b = shaftBlock(s);
    const shift =
      groupPendingChange && isSelected("shaft", i)
        ? { dx: groupPendingChange.nonRoomDx, dy: groupPendingChange.nonRoomDy }
        : { dx: 0, dy: 0 };
    const vroom = {
      x: s.x + shift.dx,
      y: s.y + shift.dy,
      width: b.width,
      height: b.height,
    };
    return (
      <React.Fragment key={`shaft-${i}`}>
        {drawRect(vroom, {
          key: `sh-${i}`,
          label: s.label || s.name,
          fill: SHAFT_FILL,
        })}
        {(s.doors || []).map((d, j) =>
          drawOpeningMarker(vroom, d, "door", `shd-${i}-${j}`)
        )}
        {(s.windows || []).map((w, j) =>
          drawOpeningMarker(vroom, w, "window", `shw-${i}-${j}`)
        )}
        {(s.open_doors || []).map((o, j) =>
          drawOpeningMarker(vroom, o, "open", `sho-${i}-${j}`)
        )}
        {(s.elevators || []).map((e, j) =>
          drawElevator(
            shift.dx || shift.dy
              ? { ...e, x: e.x + shift.dx, y: e.y + shift.dy }
              : e,
            `she-${i}-${j}`
          )
        )}
      </React.Fragment>
    );
  });

  // Render committed stairs. Same style as shafts but simpler (no
  // elevators, no windows) and a different fill colour.
  const addedStairs = (floor.stairs || []).map((s, i) => {
    const b = stairsBlock(s);
    const shift =
      groupPendingChange && isSelected("stairs", i)
        ? { dx: groupPendingChange.nonRoomDx, dy: groupPendingChange.nonRoomDy }
        : { dx: 0, dy: 0 };
    const vroom = {
      x: s.x + shift.dx,
      y: s.y + shift.dy,
      width: b.width,
      height: b.height,
    };
    return (
      <React.Fragment key={`stairs-${i}`}>
        {drawRect(vroom, {
          key: `st-${i}`,
          label: s.label || s.name,
          fill: STAIRS_FILL,
        })}
        {(s.doors || []).map((d, j) =>
          drawOpeningMarker(vroom, d, "door", `std-${i}-${j}`)
        )}
        {(s.open_doors || []).map((o, j) =>
          drawOpeningMarker(vroom, o, "open", `sto-${i}-${j}`)
        )}
      </React.Fragment>
    );
  });

  // Render committed walls: single black line from (x1,y1) to (x2,y2),
  // dot at each endpoint. Python DXF will draw the double-line 0.2m thick.
  const addedWalls = (floor.beams || []).map((w, i) => {
    const shift =
      groupPendingChange && isSelected("beam", i)
        ? { dx: groupPendingChange.nonRoomDx, dy: groupPendingChange.nonRoomDy }
        : { dx: 0, dy: 0 };
    const p1 = worldToSvg(w.x1 + shift.dx, w.y1 + shift.dy);
    const p2 = worldToSvg(w.x2 + shift.dx, w.y2 + shift.dy);
    const midX = (p1.px + p2.px) / 2;
    const midY = (p1.py + p2.py) / 2;
    return (
      <g key={`wall-${i}`}>
        <line
          x1={p1.px}
          y1={p1.py}
          x2={p2.px}
          y2={p2.py}
          stroke={ELEM_STROKE}
          strokeWidth={2}
        />
        <circle cx={p1.px} cy={p1.py} r={1.2} fill="#888" opacity={0.6} />
        <circle cx={p2.px} cy={p2.py} r={1.2} fill="#888" opacity={0.6} />
        {w.label && (
          <text
            x={midX}
            y={midY - 6}
            fontSize={11}
            fill="#555"
            textAnchor="middle"
            pointerEvents="none"
          >
            {w.label}
          </text>
        )}
      </g>
    );
  });

  // Committed SLAB panels — closed polygon. Solid = dark blue, flat = magenta.
  const addedSlabs = (floor.slabs || []).map((sl, i) => {
    const pts = (sl.points || []).map(([wx, wy]) => {
      const p = worldToSvg(wx, wy);
      return `${p.px},${p.py}`;
    }).join(" ");
    const solid = sl.kind === "solid";
    const stroke = solid ? "#1a237e" : "#c000c0";
    const fill = solid ? "rgba(26,35,126,0.10)" : "rgba(192,0,192,0.10)";
    const cx = (sl.points || []).reduce((a, p) => a + p[0], 0) / ((sl.points || []).length || 1);
    const cy = (sl.points || []).reduce((a, p) => a + p[1], 0) / ((sl.points || []).length || 1);
    const c = worldToSvg(cx, cy);
    return (
      <g key={`slab-${i}`}>
        <polygon points={pts} fill={fill} stroke={stroke} strokeWidth={1.5} />
        {(sl.label || sl.name) && (
          <text x={c.px} y={c.py} fontSize={12} fill={stroke}
            textAnchor="middle" pointerEvents="none">
            {sl.label || sl.name}
          </text>
        )}
      </g>
    );
  });

  // Committed COLUMN elements — red rectangle (width × thickness) centered
  // at (x,y), with cross-hatch fill. direction sets orientation.
  const columnCornersWorld = (c) => {
    const w = c.width, t = c.thickness;
    let hw, hh, ang;
    if (c.direction === "y") { hw = t / 2; hh = w / 2; ang = 0; }
    else if (c.direction === "angled") { hw = w / 2; hh = t / 2; ang = (c.angle || 0) * Math.PI / 180; }
    else { hw = w / 2; hh = t / 2; ang = 0; }  // "x"
    const ca = Math.cos(ang), sa = Math.sin(ang);
    return [[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]].map(([dx, dy]) => [
      c.x + (dx * ca - dy * sa),
      c.y + (dx * sa + dy * ca),
    ]);
  };

  const addedColumns = (floor.columns || []).map((c, i) => {
    const shift =
      groupPendingChange && isSelected("column", i)
        ? { dx: groupPendingChange.nonRoomDx, dy: groupPendingChange.nonRoomDy }
        : { dx: 0, dy: 0 };
    const corners = columnCornersWorld(c).map(([wx, wy]) => {
      const p = worldToSvg(wx + shift.dx, wy + shift.dy);
      return `${p.px},${p.py}`;
    }).join(" ");
    const sel = isSelected("column", i);
    const center = worldToSvg(c.x + shift.dx, c.y + shift.dy);
    return (
      <g key={`column-${i}`}>
        <polygon
          points={corners}
          fill="rgba(220,0,0,0.18)"
          stroke={sel ? "#0aa" : "#d00000"}
          strokeWidth={sel ? 3 : 2}
        />
        {(c.label || c.name) && (
          <text
            x={center.px}
            y={center.py - 6}
            fontSize={11}
            fill="#d00000"
            textAnchor="middle"
            pointerEvents="none"
          >
            {c.label || c.name}
          </text>
        )}
      </g>
    );
  });

  // Live preview of the in-progress room (dashed teal outline).
  let preview = null;
  let cornerMarker = null;
  let previewOpenings = null;
  let polygonPreview = null;
  if (drawing?.isCustom) {
    const pts = drawing.points || [];
    // If we have at least 2 points, draw the polyline of segments.
    // If we're still in the "points" phase, add a rubber-band from
    // the last placed point to the current cursor position.
    if (pts.length >= 2) {
      polygonPreview = drawPolygon(
        pts,
        // Only close visually if the user has decided to close.
        drawing.polygonPhase === "openings" && drawing.closed,
        {
          key: "custom-preview",
          fill: PREVIEW_FILL,
          stroke: PREVIEW_STROKE,
          dashed: true,
          label: drawing.label || "",
        }
      );
    }
    // Draw markers on each already-placed point so the user can see
    // exactly what they've placed. While editing, these become drag
    // handles that reshape the vertex (Stage 6a).
    const pointDots = pts.map(([x, y], i) => {
      const p = worldToSvg(x, y);
      return (
        <g key={`pdot-${i}`}>
          <circle
            cx={p.px}
            cy={p.py}
            r={isEditing ? 5.5 : 4}
            fill="white"
            stroke={PREVIEW_STROKE}
            strokeWidth={1.5}
            style={isEditing ? { cursor: "move" } : undefined}
            pointerEvents={isEditing ? "all" : "none"}
            onMouseDown={
              isEditing
                ? (e) =>
                    startElementDrag(
                      e,
                      "custom-vertex",
                      { points: pts },
                      { index: i }
                    )
                : undefined
            }
          />
          <text
            x={p.px + 7}
            y={p.py - 6}
            fontSize={10}
            fill={PREVIEW_STROKE}
            pointerEvents="none"
          >
            #{i + 1}
          </text>
        </g>
      );
    });
    // Stage 6a: whole-shape move hit-area — the filled polygon body,
    // only interactive while editing. Drawn BEFORE the vertex dots so
    // the dots (rendered after, i.e. on top) win on overlap.
    let moveHitArea = null;
    if (isEditing && pts.length >= 3) {
      const ptsAttr = pts
        .map(([px2, py2]) => {
          const s = worldToSvg(px2, py2);
          return `${s.px},${s.py}`;
        })
        .join(" ");
      moveHitArea = (
        <polygon
          key="custom-move-hit"
          points={ptsAttr}
          fill="rgba(0,0,0,0.001)"
          stroke="none"
          style={{ cursor: "move" }}
          onMouseDown={(e) =>
            startElementDrag(e, "custom-move", { points: pts })
          }
        />
      );
    }
    // Rubber-band segment from the last placed point to the cursor
    // (only while placing points, and only when we have a cursor
    // position that lies inside the bbox).
    let rubber = null;
    if (
      drawing.polygonPhase === "points" &&
      pts.length > 0 &&
      cursor &&
      cursor.x != null
    ) {
      const last = pts[pts.length - 1];
      const p1 = worldToSvg(last[0], last[1]);
      const p2 = worldToSvg(cursor.x, cursor.y);
      rubber = (
        <line
          x1={p1.px}
          y1={p1.py}
          x2={p2.px}
          y2={p2.py}
          stroke={PREVIEW_STROKE}
          strokeWidth={1.5}
          strokeDasharray="4 3"
          pointerEvents="none"
        />
      );
    }
    // In-progress custom openings (visible after user starts adding them
    // during the "openings" phase).
    let customOpeningsPreview = null;
    if (drawing.polygonPhase === "openings" && pts.length >= 2) {
      customOpeningsPreview = (
        <React.Fragment key="custom-openings-preview">
          {(drawing.doors || []).map((o, j) =>
            drawCustomOpeningMarker(pts, drawing.closed, o, "door", `cpd-${j}`)
          )}
          {(drawing.windows || []).map((o, j) =>
            drawCustomOpeningMarker(
              pts,
              drawing.closed,
              o,
              "window",
              `cpw-${j}`
            )
          )}
          {(drawing.open_doors || []).map((o, j) =>
            drawCustomOpeningMarker(pts, drawing.closed, o, "open", `cpo-${j}`)
          )}
        </React.Fragment>
      );
    }
    polygonPreview = (
      <React.Fragment key="polygon-preview-group">
        {polygonPreview}
        {moveHitArea}
        {pointDots}
        {rubber}
        {customOpeningsPreview}
      </React.Fragment>
    );
  } else if (drawing?.isShaft) {
    // Preview the shaft as a dashed rect with the COMPUTED dimensions.
    const b = shaftBlock(drawing);
    if (b.width > 0 && b.height > 0) {
      const vroom = {
        x: drawing.x,
        y: drawing.y,
        width: b.width,
        height: b.height,
      };
      preview = drawRect(vroom, {
        key: "preview",
        fill: PREVIEW_FILL,
        stroke: PREVIEW_STROKE,
        dashed: true,
        label: drawing.label || "SHAFT",
        // Stage 6a: move-only drag (no resize — steps/dims stay as
        // typed in the form), only while editing.
        onMouseDown: isEditing
          ? (e) =>
              startElementDrag(e, "block-move", { x: drawing.x, y: drawing.y })
          : undefined,
      });
      previewOpenings = (
        <React.Fragment key="preview-openings">
          {(drawing.doors || []).map((d, j) =>
            drawOpeningMarker(vroom, d, "door", `pd-${j}`)
          )}
          {(drawing.windows || []).map((w, j) =>
            drawOpeningMarker(vroom, w, "window", `pw-${j}`)
          )}
          {(drawing.open_doors || []).map((o, j) =>
            drawOpeningMarker(vroom, o, "open", `po-${j}`)
          )}
          {(drawing.elevators || []).map((e, j) => drawElevator(e, `pe-${j}`))}
        </React.Fragment>
      );
    } else if (drawing.x !== 0 || drawing.y !== 0) {
      // Corner clicked but shape params haven't produced a size yet
      const p = worldToSvg(drawing.x, drawing.y);
      cornerMarker = (
        <g key="corner-marker" pointerEvents="none">
          <line
            x1={p.px - 8}
            y1={p.py}
            x2={p.px + 8}
            y2={p.py}
            stroke={PREVIEW_STROKE}
            strokeWidth={2}
          />
          <line
            x1={p.px}
            y1={p.py - 8}
            x2={p.px}
            y2={p.py + 8}
            stroke={PREVIEW_STROKE}
            strokeWidth={2}
          />
          <circle
            cx={p.px}
            cy={p.py}
            r={4}
            fill="white"
            stroke={PREVIEW_STROKE}
            strokeWidth={2}
          />
        </g>
      );
    }
  } else if (drawing?.isBeam) {
    // Wall preview: dashed teal line from p1 to (p2 OR cursor).
    // Show a marker at p1 while waiting for p2, and rubber-band to cursor.
    const p1w = { x: drawing.x1, y: drawing.y1 };
    const havePoint1 = drawing.wallPhase !== "p1";
    if (havePoint1) {
      const p1 = worldToSvg(p1w.x, p1w.y);
      // End of the preview segment: either committed p2 or cursor.
      let endW;
      if (drawing.wallPhase === "done") {
        endW = { x: drawing.x2, y: drawing.y2 };
      } else if (cursor && cursor.x != null) {
        endW = { x: cursor.x, y: cursor.y };
      } else {
        endW = { x: drawing.x2, y: drawing.y2 };
      }
      const p2 = worldToSvg(endW.x, endW.y);
      const L = Math.hypot(endW.x - p1w.x, endW.y - p1w.y);
      const midX = (p1.px + p2.px) / 2;
      const midY = (p1.py + p2.py) / 2;
      const wallOrig = { x1: p1w.x, y1: p1w.y, x2: endW.x, y2: endW.y };
      preview = (
        <g key="wall-preview">
          {/* Stage 6a: invisible thick hit-area for whole-wall move,
              only interactive while editing. Drawn UNDER the visible
              line + endpoint handles so those still win on overlap. */}
          {isEditing && drawing.wallPhase === "done" && (
            <line
              x1={p1.px}
              y1={p1.py}
              x2={p2.px}
              y2={p2.py}
              stroke="rgba(0,0,0,0.001)"
              strokeWidth={14}
              style={{ cursor: "move" }}
              onMouseDown={(e) => startElementDrag(e, "wall-move", wallOrig)}
            />
          )}
          <line
            x1={p1.px}
            y1={p1.py}
            x2={p2.px}
            y2={p2.py}
            stroke={PREVIEW_STROKE}
            strokeWidth={2}
            strokeDasharray={drawing.wallPhase === "done" ? undefined : "6 4"}
            pointerEvents="none"
          />
          <circle
            cx={p1.px}
            cy={p1.py}
            r={isEditing ? 5.5 : 3.5}
            fill="white"
            stroke={PREVIEW_STROKE}
            strokeWidth={1.5}
            style={isEditing ? { cursor: "move" } : undefined}
            onMouseDown={
              isEditing
                ? (e) => startElementDrag(e, "wall-p1", wallOrig)
                : undefined
            }
          />
          {drawing.wallPhase === "done" && (
            <circle
              cx={p2.px}
              cy={p2.py}
              r={isEditing ? 5.5 : 3.5}
              fill="white"
              stroke={PREVIEW_STROKE}
              strokeWidth={1.5}
              style={isEditing ? { cursor: "move" } : undefined}
              onMouseDown={
                isEditing
                  ? (e) => startElementDrag(e, "wall-p2", wallOrig)
                  : undefined
              }
            />
          )}
          <text
            x={midX}
            y={midY - 6}
            fontSize={11}
            fill={PREVIEW_STROKE}
            textAnchor="middle"
            pointerEvents="none"
          >
            {L.toFixed(2)}m
          </text>
        </g>
      );
    }
  } else if (drawing?.isStairs) {
    // Preview the stairs as a dashed rect with the COMPUTED dimensions.
    const b = stairsBlock(drawing);
    if (b.width > 0 && b.height > 0) {
      const vroom = {
        x: drawing.x,
        y: drawing.y,
        width: b.width,
        height: b.height,
      };
      preview = drawRect(vroom, {
        key: "preview",
        fill: PREVIEW_FILL,
        stroke: PREVIEW_STROKE,
        dashed: true,
        label: drawing.label || "STAIRS",
        onMouseDown: isEditing
          ? (e) =>
              startElementDrag(e, "block-move", { x: drawing.x, y: drawing.y })
          : undefined,
      });
      previewOpenings = (
        <React.Fragment key="preview-openings">
          {(drawing.doors || []).map((d, j) =>
            drawOpeningMarker(vroom, d, "door", `pd-${j}`)
          )}
          {(drawing.open_doors || []).map((o, j) =>
            drawOpeningMarker(vroom, o, "open", `po-${j}`)
          )}
        </React.Fragment>
      );
    }
  } else if (drawing && drawing.width > 0 && drawing.height > 0) {
    preview = drawRect(drawing, {
      key: "preview",
      fill: PREVIEW_FILL,
      stroke: PREVIEW_STROKE,
      dashed: true,
      label: drawing.label || "",
      // Stage 6a: interior drag-to-move, only while editing.
      onMouseDown: isEditing
        ? (e) =>
            startElementDrag(e, "room-move", {
              x: drawing.x,
              y: drawing.y,
              width: drawing.width,
              height: drawing.height,
            })
        : undefined,
    });
    if (isEditing) {
      preview = (
        <React.Fragment key="preview-with-handles">
          {preview}
          {drawResizeHandles(drawing)}
        </React.Fragment>
      );
    }
    // Show the openings already added to the in-progress room so the
    // user sees them appear as they add them (they were invisible before).
    previewOpenings = (
      <React.Fragment key="preview-openings">
        {(drawing.doors || []).map((d, j) =>
          drawOpeningMarker(drawing, d, "door", `pd-${j}`)
        )}
        {(drawing.windows || []).map((w, j) =>
          drawOpeningMarker(drawing, w, "window", `pw-${j}`)
        )}
        {(drawing.open_doors || []).map((o, j) =>
          drawOpeningMarker(drawing, o, "open", `po-${j}`)
        )}
      </React.Fragment>
    );
  } else if (drawing && (drawing.x !== 0 || drawing.y !== 0)) {
    // First corner placed but no size yet — show a cross so the user
    // knows the click registered and where the corner is.
    const p = worldToSvg(drawing.x, drawing.y);
    cornerMarker = (
      <g key="corner-marker" pointerEvents="none">
        <line
          x1={p.px - 8}
          y1={p.py}
          x2={p.px + 8}
          y2={p.py}
          stroke={PREVIEW_STROKE}
          strokeWidth={2}
        />
        <line
          x1={p.px}
          y1={p.py - 8}
          x2={p.px}
          y2={p.py + 8}
          stroke={PREVIEW_STROKE}
          strokeWidth={2}
        />
        <circle
          cx={p.px}
          cy={p.py}
          r={4}
          fill="white"
          stroke={PREVIEW_STROKE}
          strokeWidth={2}
        />
      </g>
    );
  }

  // Live preview of the opening being placed:
  //   isSnap = true → outlined circle in the opening's color, larger,
  //                    signals "click here to snap exactly to this point"
  //   isSnap = false → the plain gray cursor circle
  let openingPreview = null;
  if (previewOpening) {
    const p = worldToSvg(previewOpening.x, previewOpening.y);
    if (previewOpening.isSnap) {
      const snapColor =
        previewOpening.kind === "door"
          ? DOOR_COLOR
          : previewOpening.kind === "window"
            ? WINDOW_COLOR
            : OPEN_COLOR;
      openingPreview = (
        <g pointerEvents="none">
          {/* Outer ring — the snap target */}
          <circle
            cx={p.px}
            cy={p.py}
            r={10}
            fill="none"
            stroke={snapColor}
            strokeWidth={2}
          />
          {/* Small dot inside for extra clarity */}
          <circle cx={p.px} cy={p.py} r={2.5} fill={snapColor} />
        </g>
      );
    } else {
      openingPreview = (
        <circle
          cx={p.px}
          cy={p.py}
          r={6}
          fill="none"
          stroke={PREVIEW_STROKE}
          strokeWidth={2}
          pointerEvents="none"
        />
      );
    }
  }

  // ─── Multi-select highlight overlay (Stage 3u ship 1) ───────────────
  // Draw a bright teal outline for each selected element so the user
  // can see exactly what's in the selection. Drawn AFTER all element
  // arrays so it sits on top; pointer-events disabled so clicks fall
  // through to the underlying element for further selection changes.
  const HIGHLIGHT = "#00d9c0";
  const HIGHLIGHT_W = 3;
  const isSel = (kind, index) =>
    selection.some((s) => s.kind === kind && s.index === index);

  // Stage 8f: use the EFFECTIVE geometry (pending-aware) so the dashed
  // selection box tracks the live move/resize preview instead of
  // staying frozen at the pre-drag position/size.
  const selectionHighlights = [];
  (floor.rooms || []).forEach((r0, i) => {
    if (!isSel("room", i)) return;
    const r = effectiveRoom(i);
    const a = worldToSvg(r.x, r.y);
    const b = worldToSvg(r.x + r.width, r.y + r.height);
    selectionHighlights.push(
      <rect
        key={`sel-r-${i}`}
        x={Math.min(a.px, b.px)}
        y={Math.min(a.py, b.py)}
        width={Math.abs(b.px - a.px)}
        height={Math.abs(b.py - a.py)}
        fill="none"
        stroke={HIGHLIGHT}
        strokeWidth={HIGHLIGHT_W}
        strokeDasharray="4 3"
        pointerEvents="none"
      />
    );
  });
  (floor.customs || []).forEach((c0, i) => {
    if (!isSel("custom", i)) return;
    const pts = effectiveCustomPoints(i)
      .map(([x, y]) => {
        const p = worldToSvg(x, y);
        return `${p.px},${p.py}`;
      })
      .join(" ");
    selectionHighlights.push(
      <polygon
        key={`sel-c-${i}`}
        points={pts}
        fill="none"
        stroke={HIGHLIGHT}
        strokeWidth={HIGHLIGHT_W}
        strokeDasharray="4 3"
        pointerEvents="none"
      />
    );
  });
  (floor.shafts || []).forEach((s0, i) => {
    if (!isSel("shaft", i)) return;
    const s = effectiveShaft(i);
    const b = shaftBlock(s);
    const a = worldToSvg(s.x, s.y);
    const c = worldToSvg(s.x + b.width, s.y + b.height);
    selectionHighlights.push(
      <rect
        key={`sel-sh-${i}`}
        x={Math.min(a.px, c.px)}
        y={Math.min(a.py, c.py)}
        width={Math.abs(c.px - a.px)}
        height={Math.abs(c.py - a.py)}
        fill="none"
        stroke={HIGHLIGHT}
        strokeWidth={HIGHLIGHT_W}
        strokeDasharray="4 3"
        pointerEvents="none"
      />
    );
  });
  (floor.stairs || []).forEach((s0, i) => {
    if (!isSel("stairs", i)) return;
    const s = effectiveStairs(i);
    const b = stairsBlock(s);
    const a = worldToSvg(s.x, s.y);
    const c = worldToSvg(s.x + b.width, s.y + b.height);
    selectionHighlights.push(
      <rect
        key={`sel-st-${i}`}
        x={Math.min(a.px, c.px)}
        y={Math.min(a.py, c.py)}
        width={Math.abs(c.px - a.px)}
        height={Math.abs(c.py - a.py)}
        fill="none"
        stroke={HIGHLIGHT}
        strokeWidth={HIGHLIGHT_W}
        strokeDasharray="4 3"
        pointerEvents="none"
      />
    );
  });
  (floor.beams || []).forEach((w0, i) => {
    if (!isSel("beam", i)) return;
    const w = effectiveWall(i);
    const p1 = worldToSvg(w.x1, w.y1);
    const p2 = worldToSvg(w.x2, w.y2);
    selectionHighlights.push(
      <line
        key={`sel-w-${i}`}
        x1={p1.px}
        y1={p1.py}
        x2={p2.px}
        y2={p2.py}
        stroke={HIGHLIGHT}
        strokeWidth={HIGHLIGHT_W + 2}
        strokeLinecap="round"
        opacity={0.7}
        pointerEvents="none"
      />
    );
  });

  // ─── Text labels overlay (Stage 3x.5 / 3z.1) ─────────────────────────
  // Draw each floor.texts entry at its (x, y) as a small centred label.
  // Fixed 11px so it stays readable at any zoom. When selected, draws
  // a teal outlined pill behind the text so the user sees the selection.
  // Click hit-testing lives in the page's handleCanvasClick using an
  // approximate bounding box (chars * 6px wide, 11px tall).
  // Stage 4m: hover-tooltip SVG element. Rendered LAST (on top of
  // everything) so it's always readable. We compute a rectangle wide
  // enough for the text so it doesn't get clipped.
  let hoverTooltip = null;
  if (hoverInfo) {
    const p = worldToSvg(hoverInfo.x, hoverInfo.y);
    // Position tooltip slightly above-right of cursor so it doesn't
    // sit under the mouse.
    const OFFSET_X = 12;
    const OFFSET_Y = -18;
    const PAD_X = 6;
    const PAD_Y = 3;
    const CHAR_W = 6.5; // approx px per char at 12px font
    const HEIGHT = 20;
    const w = Math.max(30, hoverInfo.text.length * CHAR_W + PAD_X * 2);
    const rx = p.px + OFFSET_X;
    const ry = p.py + OFFSET_Y;
    hoverTooltip = (
      <g pointerEvents="none">
        <rect
          x={rx}
          y={ry}
          width={w}
          height={HEIGHT}
          fill="#333"
          fillOpacity="0.9"
          rx={4}
          ry={4}
        />
        <text
          x={rx + PAD_X}
          y={ry + HEIGHT / 2 + 4}
          fontSize={12}
          fontFamily="sans-serif"
          fill="white"
        >
          {hoverInfo.text}
        </text>
      </g>
    );
  }

  // Stage 5b: mirror-line dashed preview. Spans the full visible drawing
  // area so it's clearly visible however far the user has panned/zoomed.
  let mirrorLinePreview = null;
  if (mirrorPreview) {
    const { axis, line } = mirrorPreview;
    const lineVal = Number(line) || 0;
    if (axis === "vertical") {
      const p1 = worldToSvg(lineVal, offsetY);
      const p2 = worldToSvg(lineVal, offsetY + bbox.height);
      mirrorLinePreview = (
        <g pointerEvents="none">
          <line
            x1={p1.px}
            y1={p1.py}
            x2={p2.px}
            y2={p2.py}
            stroke="#2a3d8f"
            strokeWidth={2}
            strokeDasharray="8 5"
          />
          <text
            x={p1.px + 6}
            y={p1.py - 6}
            fontSize={12}
            fontWeight={600}
            fill="#2a3d8f"
          >
            x = {lineVal}
          </text>
        </g>
      );
    } else {
      const p1 = worldToSvg(offsetX, lineVal);
      const p2 = worldToSvg(offsetX + bbox.width, lineVal);
      mirrorLinePreview = (
        <g pointerEvents="none">
          <line
            x1={p1.px}
            y1={p1.py}
            x2={p2.px}
            y2={p2.py}
            stroke="#2a3d8f"
            strokeWidth={2}
            strokeDasharray="8 5"
          />
          <text
            x={p1.px + 6}
            y={p1.py - 6}
            fontSize={12}
            fontWeight={600}
            fill="#2a3d8f"
          >
            y = {lineVal}
          </text>
        </g>
      );
    }
  }

  // Stage 5d: paste-position ghost preview. Shows a dashed outline of
  // the copied selection's combined bounding box, translated to the
  // current (clicked / fine-tuned) paste target position.
  let pastePreviewRect = null;
  if (pastePreview && pastePreview.items && pastePreview.items.length > 0) {
    const dx = (Number(pastePreview.x) || 0) - pastePreview.refX;
    const dy = (Number(pastePreview.y) || 0) - pastePreview.refY;
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (const { kind, data } of pastePreview.items) {
      if (kind === "room") {
        minX = Math.min(minX, data.x);
        maxX = Math.max(maxX, data.x + data.width);
        minY = Math.min(minY, data.y);
        maxY = Math.max(maxY, data.y + data.height);
      } else if (kind === "beam") {
        minX = Math.min(minX, data.x1, data.x2);
        maxX = Math.max(maxX, data.x1, data.x2);
        minY = Math.min(minY, data.y1, data.y2);
        maxY = Math.max(maxY, data.y1, data.y2);
      } else if (kind === "custom") {
        for (const [px, py] of data.points || []) {
          minX = Math.min(minX, px);
          maxX = Math.max(maxX, px);
          minY = Math.min(minY, py);
          maxY = Math.max(maxY, py);
        }
      } else if (kind === "shaft" || kind === "stairs") {
        // Approximate footprint — exact block dims aren't available here
        // without importing shaftDerived/stairsDerived; a small fixed
        // margin keeps the ghost box roughly the right size.
        minX = Math.min(minX, data.x);
        maxX = Math.max(maxX, data.x + 2);
        minY = Math.min(minY, data.y);
        maxY = Math.max(maxY, data.y + 2);
      }
    }
    if (isFinite(minX)) {
      const a = worldToSvg(minX + dx, minY + dy);
      const b = worldToSvg(maxX + dx, maxY + dy);
      const rx = Math.min(a.px, b.px),
        ry = Math.min(a.py, b.py);
      const rw = Math.abs(b.px - a.px),
        rh = Math.abs(b.py - a.py);
      pastePreviewRect = (
        <g pointerEvents="none">
          <rect
            x={rx}
            y={ry}
            width={rw}
            height={rh}
            fill="#5e35b1"
            fillOpacity="0.12"
            stroke="#5e35b1"
            strokeWidth={2}
            strokeDasharray="6 4"
          />
        </g>
      );
    }
  }

  const textLabels = [];
  if (showTexts) {
    (floor.texts || []).forEach((t, i) => {
      if (!t || typeof t.x !== "number" || typeof t.y !== "number") return;
      const p = worldToSvg(t.x, t.y);
      const selected = isSel("text", i);
      const label = t.content || "";
      // Approx width for the highlight pill: 6px per character + padding
      const w = Math.max(24, label.length * 6 + 8);
      const h = 14;
      textLabels.push(
        <g key={`txt-${i}`} pointerEvents="none">
          {selected && (
            <rect
              x={p.px - w / 2}
              y={p.py - h / 2}
              width={w}
              height={h}
              rx={3}
              ry={3}
              fill="#d4f6f0"
              stroke={HIGHLIGHT}
              strokeWidth={1.5}
            />
          )}
          <text
            x={p.px}
            y={p.py}
            fontSize={11}
            fill={selected ? "#005f52" : "#666"}
            fontWeight={selected ? 600 : 400}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {label}
          </text>
        </g>
      );
    });
  }

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1,
        position: "relative",
        minHeight: 0,
        minWidth: 0, // lets it shrink inside a flex column
        overflow: "hidden",
      }}
    >
      <svg
        width={size.w}
        height={size.h}
        viewBox={`0 0 ${size.w} ${size.h}`}
        preserveAspectRatio="xMidYMid meet"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleLeave}
        onClick={handleClick}
        onWheel={handleWheel}
        style={{
          background: "white",
          cursor: panRef.current
            ? "grabbing"
            : hasActiveTool
              ? "crosshair"
              : hoverCursor || "default",
          userSelect: "none",
        }}
      >
        {grid}
        {/* Bbox outline */}
        <rect
          x={originX}
          y={originY - drawH}
          width={drawW}
          height={drawH}
          fill="none"
          stroke={AXIS}
          strokeWidth={1.5}
        />
        {addedElements}
        {addedCustoms}
        {addedShafts}
        {addedStairs}
        {addedSlabs}
        {addedWalls}
        {addedColumns}
        {selectionHighlights}
        {textLabels}
        {preview}
        {polygonPreview}
        {previewOpenings}
        {cornerMarker}
        {openingPreview}

        {/* Axis labels */}
        <text
          x={worldToSvg(offsetX + bbox.width, offsetY).px + 6}
          y={worldToSvg(offsetX + bbox.width, offsetY).py + 4}
          fontSize={11}
          fill={AXIS}
        >
          x = {bbox.width}m
        </text>
        <text
          x={worldToSvg(offsetX, offsetY + bbox.height).px - 6}
          y={worldToSvg(offsetX, offsetY + bbox.height).py - 6}
          fontSize={11}
          fill={AXIS}
          textAnchor="end"
        >
          y = {bbox.height}m
        </text>
        <text
          x={worldToSvg(0, 0).px - 6}
          y={worldToSvg(0, 0).py + 14}
          fontSize={11}
          fill={AXIS}
          textAnchor="end"
        >
          (0,0)
        </text>
        {mirrorLinePreview}
        {pastePreviewRect}
        {hoverTooltip}
        {dragSelectBox && (
          <rect
            x={dragSelectBox.x1}
            y={dragSelectBox.y1}
            width={dragSelectBox.x2 - dragSelectBox.x1}
            height={dragSelectBox.y2 - dragSelectBox.y1}
            fill="#2e8b57"
            fillOpacity="0.12"
            stroke="#2e8b57"
            strokeWidth={1.5}
            strokeDasharray="6 4"
            pointerEvents="none"
          />
        )}
      </svg>

      {/* Bottom-left cursor readout */}
      <div
        style={{
          position: "absolute",
          bottom: 8,
          left: 8,
          background: "rgba(0,0,0,0.7)",
          color: "white",
          padding: "4px 8px",
          borderRadius: 4,
          fontSize: 12,
          fontFamily: "monospace",
          pointerEvents: "none",
        }}
      >
        {cursor.x !== null
          ? `x = ${cursor.x.toFixed(2)}m,  y = ${cursor.y.toFixed(2)}m` +
            `  |  grid = ${gridStep >= 1 ? "1 m" : gridStep === 0.1 ? "10 cm" : "1 cm"}` +
            `  |  mode: ${clickMode}`
          : `zoom = ${zoom.toFixed(2)}×  |  grid = ${gridStep >= 1 ? "1 m" : gridStep === 0.1 ? "10 cm" : "1 cm"}`}
      </div>

      {/* Top-right zoom controls */}
      <div
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          display: "flex",
          gap: 4,
        }}
      >
        <button
          onClick={() => setZoom((z) => Math.max(0.5, z / 1.3))}
          style={btnStyle}
        >
          −
        </button>
        <button onClick={resetView} style={btnStyle}>
          Fit
        </button>
        <button
          onClick={() => setZoom((z) => Math.min(120, z * 1.3))}
          style={btnStyle}
        >
          +
        </button>
      </div>
    </div>
  );
}

const btnStyle = {
  padding: "4px 10px",
  background: "white",
  border: "1px solid #ccc",
  borderRadius: 4,
  cursor: "pointer",
  fontSize: 14,
  fontWeight: 600,
};
